import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Icon } from '@bloo-team/ui'
import { ChevronDown, ChevronRight, ExternalLink, Pin, X } from '@bloo-team/ui/icons'
import { RawEventView, ResultsTable, TimelineChart, shortTime, useTimelineJobs } from '@bloo-team/core-shell'
import {
  displayValue,
  fieldNamed,
  formatCell,
  isLogEventField,
  stripDuration,
  type SearchRow,
  type SearchWindow,
} from '@bloo-team/api-client'
import { useSearchJob } from '@bloo-team/core-shell'
import { toLocalApiTime } from '@bloo-team/core-shell'
import { classifyEntity, nodeId, type EntityKind, type GraphNode, type HuntGraph } from '../../lib/hunt-graph'
import { GraphCanvas } from '../hunt/GraphCanvas'
import { entityActivityQuery, sameEntitySignalsQuery } from '../../lib/signal-hunt'
import type { Signal } from '../../lib/signal-model'
import type { DeepPanelProps } from './types'
import { baseName, buildProcessTree, countNodes, type ProcessNode } from './process-tree'

/**
 * The signal deep panel — investigate tier 1 (docs/signals-requirements.md
 * §8, decisions S2/S5): an inline, full-width expansion of the signal panel
 * with no hunt object behind it. Five stacked, collapsible sections:
 *
 *   1. raw-event timeline  — `signal.rawEventQuery` re-run ±N minutes around
 *                            the signal, the shared TimelineChart + ResultsTable
 *   2. related signals     — the storyline's members (or same-entity signals) on a time strip, offsets from
 *                            this signal
 *   3. mini pivot graph    — read-only GraphCanvas over the signals' entities
 *   4. causality tree      — parent/child process forest, data-dependent
 *   5. notes with pin      — note editor + the pinned raw-event excerpts
 *
 * Props-in/callbacks-out (components/search/index.ts rule): the host page
 * owns the triage store, the route and the hunt creation; this component owns
 * exactly one search job (the raw events) and its shadow timeline job.
 */

/** ±N minutes offered around the signal time. */
export const WINDOW_MINUTES = [5, 15, 60, 240] as const
export type WindowMinutes = (typeof WINDOW_MINUTES)[number]

/**
 * The absolute window ±`minutes` around `at`, spelled the way the search
 * page's picker sends it (local `YYYY-MM-DDTHH:mm:ss`, see toLocalApiTime /
 * @bloo-team/api-client toApiTime).
 */
export function signalWindow(at: number, minutes: number): SearchWindow {
  return { start: toLocalApiTime(at - minutes * 60_000), end: toLocalApiTime(at + minutes * 60_000) }
}

/** `−9 min`, `+2 min`, `+1 h 30 min`, `now` — the related strip's offsets. */
export function offsetLabel(deltaMs: number): string {
  const min = Math.round(deltaMs / 60_000)
  if (min === 0) return 'now'
  const sign = min < 0 ? '−' : '+'
  const a = Math.abs(min)
  if (a < 120) return `${sign}${a} min`
  const h = Math.floor(a / 60)
  const m = a % 60
  return `${sign}${h} h${m ? ` ${m} min` : ''}`
}

/** Signal entity field -> graph kind; anything else falls to the value heuristics. */
const KIND_BY_FIELD: Record<string, EntityKind> = {
  user: 'user',
  host: 'host',
  ip: 'ip',
  url: 'domain',
  domain: 'domain',
  hash: 'hash',
}

/** Kind for a signal entity: the field name first, the value's shape second. */
export function kindForEntity(field: string, value: string): EntityKind {
  return KIND_BY_FIELD[field.toLowerCase()] ?? classifyEntity(field, value) ?? 'other'
}

/**
 * The mini pivot graph: one node per distinct entity across the signals,
 * `summary.hits` = how many signals carried it; one edge between entities
 * that co-occur on a signal (first entity -> each other, deduped), tagged
 * with the same-entity SIGNALS query that would reproduce the link. Pure.
 */
export function buildSignalGraph(signals: readonly Signal[]): HuntGraph {
  const nodes = new Map<string, GraphNode & { first: number; last: number }>()
  const edges: HuntGraph['edges'] = []
  const edgeKeys = new Set<string>()
  for (const s of signals) {
    const ids: string[] = []
    for (const e of s.entities) {
      const kind = kindForEntity(e.field, e.value)
      const id = nodeId(kind, e.value)
      const cur = nodes.get(id)
      if (cur) {
        cur.summary!.hits += 1
        cur.first = Math.min(cur.first, s.at)
        cur.last = Math.max(cur.last, s.at)
      } else {
        nodes.set(id, {
          id,
          kind,
          value: e.value,
          field: e.field,
          expanded: false,
          summary: { hits: 1, firstSeen: null, lastSeen: null },
          first: s.at,
          last: s.at,
        })
      }
      if (!ids.includes(id)) ids.push(id)
    }
    const [head, ...rest] = ids
    if (!head) continue
    const via = sameEntitySignalsQuery(s)
    for (const to of rest) {
      const k = `${head}->${to}`
      if (edgeKeys.has(k) || edgeKeys.has(`${to}->${head}`)) continue
      edgeKeys.add(k)
      edges.push({ from: head, to, viaQuery: via })
    }
  }
  return {
    nodes: [...nodes.values()].map(({ first, last, ...n }) => ({
      ...n,
      summary: { hits: n.summary!.hits, firstSeen: new Date(first).toISOString(), lastSeen: new Date(last).toISOString() },
    })),
    edges,
  }
}

/* ---------------------------------------------------------------- section */

function Section({
  id,
  title,
  meta,
  open,
  onToggle,
  actions,
  children,
}: {
  id: string
  title: string
  meta?: string
  open: boolean
  onToggle: () => void
  actions?: ReactNode
  children: ReactNode
}) {
  const bodyId = `sdp-${id}`
  return (
    <section className="sdp-section" data-open={open || undefined}>
      <div className="sdp-head">
        <button
          type="button"
          className="sdp-toggle hud-eyebrow"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <Icon icon={open ? ChevronDown : ChevronRight} size={11} />
          {title}
          {meta ? <b>{meta}</b> : null}
        </button>
        {actions ? <span className="hud-row sdp-actions">{actions}</span> : null}
      </div>
      {open ? (
        <div id={bodyId} className="sdp-body hud-stack">
          {children}
        </div>
      ) : null}
    </section>
  )
}

/* ------------------------------------------------------------ process tree */

function ProcessBranch({ node, depth }: { node: ProcessNode; depth: number }) {
  return (
    <li className="sdp-proc" data-synthetic={node.synthetic || undefined}>
      <div className="sdp-proc-row">
        <span className="sdp-proc-image" title={node.image}>
          {baseName(node.image)}
        </span>
        {node.pid ? <span className="hud-unit">pid {node.pid}</span> : null}
        {node.synthetic ? <span className="hud-unit">parent only</span> : null}
      </div>
      {node.commandLine ? <code className="sdp-proc-cmd mono">{node.commandLine}</code> : null}
      {node.children.length && depth < 32 ? (
        <ul className="sdp-tree">
          {node.children.map((c) => (
            <ProcessBranch key={c.id} node={c} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

/* ------------------------------------------------------------------ panel */

/** Every populated field of a row, values trimmed — the pin excerpt (quota rule). */
function excerptOf(row: SearchRow, fields: readonly string[], max = 200): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of fields) {
    const v = row[f]
    if (v === null || v === undefined || v === '') continue
    const s = typeof v === 'string' ? v.trim() : String(v)
    if (!s) continue
    out[f] = s.length > max ? `${s.slice(0, max - 1)}…` : s
  }
  return out
}

type SectionId = 'raw' | 'related' | 'graph' | 'tree' | 'notes'

/**
 * The deep panel. One raw-event job runs on mount and again whenever the
 * signal or the ±window changes; the hook revokes a still-running task on
 * unmount. See the module comment for the section list.
 */
export function DeepPanel({
  clusterId,
  scopeId,
  signal,
  triage,
  related,
  user,
  onPin,
  onUnpin,
  onAddNote,
  onOpenSearch,
  onOpenAsHunt,
  onSelectSignal,
}: DeepPanelProps) {
  const [open, setOpen] = useState<Record<SectionId, boolean>>({
    raw: true,
    related: true,
    graph: true,
    tree: true,
    notes: true,
  })
  const toggle = (id: SectionId) => setOpen((o) => ({ ...o, [id]: !o[id] }))

  /* --- 1. raw-event timeline ------------------------------------------------ */

  const [minutes, setMinutes] = useState<WindowMinutes>(15)
  // Live signal documents carry NO raw_event_query (verified 2026-08-24,
  // docs/inventory/signals-capture.json) — only the workbook emission contract
  // does. Fall back to the entity-activity query on the signal's source
  // stream, then to the same-entity SIGNALS query, so the timeline always
  // has something to run.
  const rawQuery = (signal.rawEventQuery || entityActivityQuery(signal) || sameEntitySignalsQuery(signal)).trim()
  // The signal's query carries its own `duration`; the ±window replaces it
  // the way the search page's zoom does. A query whose quoted literals defeat
  // the stage split keeps its duration and runs without a window.
  const stripped = useMemo(() => (rawQuery ? stripDuration(rawQuery) : null), [rawQuery])
  const query = stripped ?? rawQuery
  const win = useMemo(
    () => (stripped ? signalWindow(signal.at, minutes) : undefined),
    [stripped, signal.at, minutes],
  )

  const job = useSearchJob({ clusterId, scopeId })
  const ranFor = useRef<string | null>(null)
  useEffect(() => {
    if (!query) return
    // Keyed on signal + window so a re-render never re-submits, and a change
    // to either always does. `job` changes identity per render; the key gates.
    const key = `${signal.uuid}|${minutes}`
    if (ranFor.current === key) return
    ranFor.current = key
    job.run(query, win)
  }, [query, win, minutes, signal.uuid, job])

  const tl = useTimelineJobs({ clusterId, scopeId, job, window: win, displayValue })
  const page = job.page
  const rows = useMemo(() => page?.rows ?? [], [page])
  const columns = job.columns.slice(0, 20)
  const timeField = page ? fieldNamed(page.fields, 'CNAMTime') : null
  const busy = job.phase === 'submitting' || job.phase === 'running'

  // The open row / raw view are remembered WITH the task they belong to, so
  // a new result drops them without an effect — a row from the previous
  // result must never sit open under the next table.
  const [detail, setDetail] = useState<{
    taskId: string | null
    row: SearchRow | null
    raw: { field: string; text: string } | null
  }>({ taskId: null, row: null, raw: null })
  const openRow = detail.taskId === job.taskId ? detail.row : null
  const rawView = detail.taskId === job.taskId ? detail.raw : null
  const setOpenRow = (row: SearchRow | null) => setDetail({ taskId: job.taskId, row, raw: null })
  const setRawView = (raw: { field: string; text: string } | null) => setDetail({ taskId: job.taskId, row: null, raw })

  const pinRow = (row: SearchRow) => {
    if (!page) return
    onPin({ excerpt: excerptOf(row, page.fields), sourceQuery: job.ranQuery ?? query })
    setOpenRow(null)
  }

  const showTimeline =
    tl.current && rows.length > 0 && tl.phase === 'done' && tl.slice !== null && tl.buckets.length > 1
  const markerPct = (() => {
    if (!showTimeline || !tl.slice) return null
    const t0 = tl.buckets[0]!.t
    const t1 = tl.buckets[tl.buckets.length - 1]!.t + tl.slice.ms
    if (t1 <= t0) return null
    return Math.min(100, Math.max(0, ((signal.at - t0) / (t1 - t0)) * 100))
  })()

  const canPivot = () => false
  const logField = openRow && page ? page.fields.find((f) => isLogEventField(f) && typeof openRow[f] === 'string' && openRow[f]) : undefined

  /* --- 2. related ------------------------------------------------------------ */

  const strip = useMemo(() => {
    // `related` may or may not include the signal itself — dedupe by uuid.
    const byUuid = new Map<string, Signal>()
    for (const s of [signal, ...related]) if (!byUuid.has(s.uuid)) byUuid.set(s.uuid, s)
    return [...byUuid.values()].sort((a, b) => a.at - b.at)
  }, [signal, related])

  /* --- 3. mini graph --------------------------------------------------------- */

  const graph = useMemo(() => buildSignalGraph(strip), [strip])
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const node = graph.nodes.find((n) => n.id === selectedNode) ?? null

  /* --- 4. causality tree ----------------------------------------------------- */

  const tree = useMemo(() => (page ? buildProcessTree(page.rows, page.fields) : null), [page])

  /* --- 5. notes -------------------------------------------------------------- */

  const [note, setNote] = useState('')
  const addNote = () => {
    const text = note.trim()
    if (!text) return
    onAddNote(text)
    setNote('')
  }
  const pins = triage?.pins ?? []

  return (
    <div className="sdp hud-stack" data-testid="signal-deep-panel">
      {/* ------------------------------------------------ 1. raw events */}
      <Section
        id="raw"
        title="raw events"
        meta={page && job.phase === 'done' ? `${page.total.toLocaleString('en-US')} events` : undefined}
        open={open.raw}
        onToggle={() => toggle('raw')}
        actions={
          <>
            <span className="q-mode sdp-window" role="group" aria-label="Window around the signal">
              {WINDOW_MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  className="list-chip"
                  aria-pressed={minutes === m}
                  disabled={!stripped}
                  onClick={() => setMinutes(m)}
                >
                  ±{m >= 60 ? `${m / 60} h` : `${m} min`}
                </button>
              ))}
            </span>
            {busy ? (
              <button type="button" className="list-chip" onClick={job.cancel}>
                cancel
              </button>
            ) : null}
            <button
              type="button"
              className="list-chip"
              disabled={!rawQuery}
              title="Open this query in Datafabric · Search"
              onClick={() => onOpenSearch(rawQuery)}
            >
              <Icon icon={ExternalLink} size={10} /> open in search
            </button>
          </>
        }
      >
        {rawQuery ? (
          <code className="sdp-query mono" title={rawQuery}>
            {query}
            {win ? <span className="hud-unit"> · {win.start.slice(11, 16)} → {win.end.slice(11, 16)}</span> : null}
          </code>
        ) : (
          <p className="hud-unit" role="status">
            This signal carries no raw-event query — nothing to replay.
          </p>
        )}
        {!stripped && rawQuery ? (
          <span className="q-facet-note">the query keeps its own duration — the ±window does not apply</span>
        ) : null}

        {job.queryError ? (
          <div className="bui-alert bui-alert-error" role="alert">
            <span className="bui-alert-mark">✕</span>
            <span>
              <span className="bui-alert-title">The engine refused the raw-event query</span>
              {job.queryError.message}
              <span className="sdp-alert-actions">
                <button type="button" className="list-chip" onClick={() => onOpenSearch(rawQuery)}>
                  <Icon icon={ExternalLink} size={10} /> open in search
                </button>
              </span>
            </span>
          </div>
        ) : null}
        {job.error ? (
          <div className="bui-alert bui-alert-error" role="alert">
            <span className="bui-alert-mark">✕</span>
            <span>
              <span className="bui-alert-title">The search could not run</span>
              {job.error.message}
              <span className="sdp-alert-actions">
                <button type="button" className="list-chip" onClick={() => onOpenSearch(rawQuery)}>
                  <Icon icon={ExternalLink} size={10} /> open in search
                </button>
              </span>
            </span>
          </div>
        ) : null}

        {rawQuery && !job.queryError && !job.error ? (
          <span className="hud-unit" role="status">
            {job.phase === 'submitting' ? 'submitting…' : null}
            {job.phase === 'running' ? 'running — the query is executing on the cluster' : null}
            {job.phase === 'failed' ? 'the query was cancelled or the engine failed to complete it' : null}
            {job.phase === 'done' && page
              ? `${page.total.toLocaleString('en-US')} events · signal at ${shortTime(signal.at)}`
              : null}
          </span>
        ) : null}

        {showTimeline && tl.slice ? (
          <div className="sdp-tl">
            <TimelineChart
              buckets={tl.buckets}
              stack={tl.stack ?? undefined}
              sliceMs={tl.slice.ms}
              style={tl.style}
              onSelectRange={() => {}}
            />
            {markerPct !== null ? (
              <span
                className="sdp-tl-marker"
                style={{ left: `${markerPct}%` }}
                aria-label={`Signal time ${shortTime(signal.at)}`}
                title={`signal · ${shortTime(signal.at)}`}
              />
            ) : null}
          </div>
        ) : null}

        {page && job.phase === 'done' ? (
          rows.length === 0 ? (
            <p className="hud-unit" role="status">
              Nothing in this window. Widen it, or open the query in search.
            </p>
          ) : (
            <ResultsTable
              columns={columns}
              rows={rows}
              openRow={openRow}
              onOpenRow={(r) => setOpenRow(openRow === r ? null : r)}
              onRawView={(field, text) => setRawView({ field, text })}
              canPivot={canPivot}
              onPivot={() => {}}
            />
          )
        ) : null}

        {openRow && page ? (
          <div className="hud-panel sdp-row" data-testid="sdp-row-detail">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">
                event{timeField ? ` · ${formatCell(timeField, openRow[timeField])}` : ''}
              </span>
              <span className="hud-row sdp-actions">
                <button type="button" className="bui-btn bui-btn-solid bui-btn-sm" onClick={() => pinRow(openRow)}>
                  <Icon icon={Pin} size={12} /> Pin
                </button>
                <button
                  type="button"
                  className="qbc-x"
                  aria-label="Close event detail"
                  onClick={() => setOpenRow(null)}
                >
                  <Icon icon={X} size={10} />
                </button>
              </span>
            </div>
            <dl className="kv">
              {page.fields
                .filter((f) => openRow[f] !== null && openRow[f] !== undefined && openRow[f] !== '' && !isLogEventField(f))
                .map((f) => (
                  <div key={f} style={{ display: 'contents' }}>
                    <dt>{f}</dt>
                    <dd className="mono">{formatCell(f, openRow[f])}</dd>
                  </div>
                ))}
            </dl>
            {logField ? <RawEventView text={openRow[logField] as string} /> : null}
          </div>
        ) : rawView ? (
          <div className="hud-panel sdp-row">
            <div className="hud-row hud-row-between">
              <span className="hud-eyebrow">{rawView.field}</span>
              <button type="button" className="qbc-x" aria-label="Close raw event" onClick={() => setRawView(null)}>
                <Icon icon={X} size={10} />
              </button>
            </div>
            <RawEventView text={rawView.text} />
          </div>
        ) : null}
      </Section>

      {/* ------------------------------------------------ 2. related */}
      <Section
        id="related"
        title="related signals"
        meta={`${strip.length - 1}`}
        open={open.related}
        onToggle={() => toggle('related')}
      >
        {strip.length <= 1 ? (
          <p className="hud-unit" role="status">
            No other signal shares an entity with this one in the window.
          </p>
        ) : (
          <ol className="sdp-related" aria-label="Related signals by time">
            {strip.map((s) => {
              const current = s.uuid === signal.uuid
              return (
                <li key={s.uuid} data-current={current || undefined} data-severity-inline={s.severity}>
                  <span className="sdp-offset mono">{current ? 'this' : offsetLabel(s.at - signal.at)}</span>
                  {current ? (
                    <span className="sdp-related-name" aria-current="true">
                      {s.detectionName || s.uuid}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="sdp-related-name q-facet-value"
                      title={`${s.detectionName} · ${shortTime(s.at)}`}
                      onClick={() => onSelectSignal(s.uuid)}
                    >
                      <span className="q-facet-v">{s.detectionName || s.uuid}</span>
                      <span className="q-facet-n">{s.severity}</span>
                    </button>
                  )}
                  <span className="hud-unit">{shortTime(s.at)}</span>
                </li>
              )
            })}
          </ol>
        )}
      </Section>

      {/* ------------------------------------------------ 3. mini graph */}
      <Section
        id="graph"
        title="pivot graph"
        meta={graph.nodes.length ? `${graph.nodes.length} entities` : undefined}
        open={open.graph}
        onToggle={() => toggle('graph')}
        actions={
          <button type="button" className="list-chip" onClick={onOpenAsHunt} title="Open this signal as a hunt">
            <Icon icon={ExternalLink} size={10} /> expand in a hunt
          </button>
        }
      >
        {graph.nodes.length === 0 ? (
          <p className="hud-unit" role="status">
            This signal names no entities — nothing to graph.
          </p>
        ) : (
          <div className="sdp-graph">
            <GraphCanvas graph={graph} selected={selectedNode} onSelect={setSelectedNode} />
            {node ? (
              <div className="hud-panel sdp-node">
                <div className="hud-stack" style={{ gap: 'var(--bloo-space-1)' }}>
                  <span className="hud-eyebrow">{node.kind}</span>
                  <span className="mono">{node.value}</span>
                  <span className="q-facet-note">
                    {node.summary
                      ? `${node.summary.hits} signal${node.summary.hits === 1 ? '' : 's'} · field ${node.field}`
                      : `field ${node.field}`}
                  </span>
                  <div>
                    <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={onOpenAsHunt}>
                      Expand in a hunt
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <span className="q-facet-note">read-only — select a node for its summary; expanding pivots happens in a hunt</span>
            )}
          </div>
        )}
      </Section>

      {/* ------------------------------------------------ 4. causality tree */}
      {tree && tree.length ? (
        <Section
          id="tree"
          title="causality"
          meta={`${countNodes(tree)} processes`}
          open={open.tree}
          onToggle={() => toggle('tree')}
        >
          <ul className="sdp-tree sdp-tree-root" aria-label="Process tree">
            {tree.map((n) => (
              <ProcessBranch key={n.id} node={n} depth={0} />
            ))}
          </ul>
        </Section>
      ) : null}

      {/* ------------------------------------------------ 5. notes + pins */}
      <Section
        id="notes"
        title="notes"
        meta={pins.length ? `${pins.length} pinned` : undefined}
        open={open.notes}
        onToggle={() => toggle('notes')}
      >
        <div className="hud-stack" style={{ gap: 'var(--bloo-space-1)' }}>
          <textarea
            className="bui-input"
            aria-label="New note"
            placeholder={`note as ${user}…`}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                addNote()
              }
            }}
          />
          <div>
            <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" disabled={!note.trim()} onClick={addNote}>
              Add note
            </button>
          </div>
        </div>
        {pins.length === 0 ? (
          <p className="q-facet-note" role="status">
            Nothing pinned yet — open a raw event above and pin it.
          </p>
        ) : (
          <ul className="sdp-pins" aria-label="Pinned rows">
            {pins.map((p) => {
              const entries = Object.entries(p.excerpt)
              return (
                <li key={p.id} className="hud-panel sdp-pin">
                  <div className="hud-row hud-row-between">
                    <span className="hud-eyebrow">pinned · {shortTime(p.at)}</span>
                    <button type="button" className="qbc-x" aria-label={`Unpin row ${p.id}`} onClick={() => onUnpin(p.id)}>
                      <Icon icon={X} size={10} />
                    </button>
                  </div>
                  <dl className="kv">
                    {entries.slice(0, 4).map(([k, v]) => (
                      <div key={k} style={{ display: 'contents' }}>
                        <dt>{k}</dt>
                        <dd className="mono">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  {entries.length > 4 ? <span className="hud-unit">+{entries.length - 4} more fields</span> : null}
                  {p.sourceQuery ? (
                    <button type="button" className="list-chip" title={p.sourceQuery} onClick={() => onOpenSearch(p.sourceQuery)}>
                      <Icon icon={ExternalLink} size={10} /> source query
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </Section>
    </div>
  )
}
